import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, getAuthHeader } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { Plus, LogOut, Users, ClipboardList, Send, ChevronRight, Leaf, Plane, Waves } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PROGRAM_ICONS = { "AgriLearn Campus": Leaf, "AeroLab Experience": Plane, "SailScience Program": Waves };

export default function PortalDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tours, setTours] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", program_type: "AgriLearn Campus", visit_date: "", notes: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchTours(); }, []);

  const fetchTours = async () => {
    try {
      const { data } = await axios.get(`${API}/api/portal/tours`, { headers: getAuthHeader() });
      setTours(data);
    } catch { toast.error("Failed to load tours"); }
    finally { setLoading(false); }
  };

  const createTour = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Tour name is required"); return; }
    try {
      await axios.post(`${API}/api/portal/tours`, form, { headers: getAuthHeader() });
      toast.success("Tour created!");
      setShowCreate(false);
      setForm({ name: "", program_type: "AgriLearn Campus", visit_date: "", notes: "" });
      fetchTours();
    } catch { toast.error("Failed to create tour"); }
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const statusColors = { draft: "bg-yellow-100 text-yellow-700", submitted: "bg-blue-100 text-blue-700", completed: "bg-green-100 text-green-700" };

  return (
    <div className="min-h-screen bg-background" data-testid="portal-dashboard">
      {/* Header */}
      <header className="bg-primary text-primary-foreground px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-heading text-xl font-medium">OSAA School Portal</h1>
            <p className="text-sm text-primary-foreground/70">Welcome, {user?.name || "User"}</p>
          </div>
          <div className="flex items-center gap-3">
            {user?.role === "super_admin" && (
              <Link to="/admin" className="text-sm text-primary-foreground/80 hover:text-primary-foreground border border-primary-foreground/30 px-3 py-1.5 rounded-sm">
                Admin Panel
              </Link>
            )}
            <button onClick={handleLogout} className="inline-flex items-center gap-1.5 text-sm text-primary-foreground/80 hover:text-primary-foreground">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-card border border-border/60 p-5 rounded-sm">
            <ClipboardList className="w-5 h-5 text-accent mb-2" />
            <div className="font-heading text-2xl font-semibold text-foreground">{tours.length}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Total Tours</div>
          </div>
          <div className="bg-card border border-border/60 p-5 rounded-sm">
            <Users className="w-5 h-5 text-accent mb-2" />
            <div className="font-heading text-2xl font-semibold text-foreground">{tours.reduce((a, t) => a + (t.student_count || 0), 0)}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Total Students</div>
          </div>
          <div className="bg-card border border-border/60 p-5 rounded-sm">
            <Send className="w-5 h-5 text-accent mb-2" />
            <div className="font-heading text-2xl font-semibold text-foreground">{tours.filter(t => t.status === "submitted").length}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Submitted</div>
          </div>
          <div className="bg-card border border-border/60 p-5 rounded-sm">
            <div className="w-5 h-5 bg-yellow-400 rounded-full mb-2" />
            <div className="font-heading text-2xl font-semibold text-foreground">{tours.filter(t => t.status === "draft").length}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Drafts</div>
          </div>
        </div>

        {/* Create Tour */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-medium text-foreground">Your Tours</h2>
          <button onClick={() => setShowCreate(!showCreate)} data-testid="create-tour-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground text-sm font-medium rounded-sm hover:bg-accent/90">
            <Plus className="w-4 h-4" /> New Tour
          </button>
        </div>

        {showCreate && (
          <div className="bg-card border border-border/60 p-6 rounded-sm mb-6" data-testid="create-tour-form">
            <h3 className="font-heading text-lg font-medium mb-4">Create New Tour</h3>
            <form onSubmit={createTour} className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium mb-1 block">Tour Name *</label>
                <Input placeholder="e.g. Grade 8 Farm Visit" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">Program</label>
                <select value={form.program_type} onChange={e => setForm(p => ({...p, program_type: e.target.value}))}
                  className="w-full h-10 border border-border/60 rounded-sm px-3 text-sm bg-card">
                  <option>AgriLearn Campus</option>
                  <option>AeroLab Experience</option>
                  <option>SailScience Program</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">Visit Date</label>
                <Input type="date" value={form.visit_date} onChange={e => setForm(p => ({...p, visit_date: e.target.value}))} />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">Notes</label>
                <Input placeholder="Any notes..." value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} />
              </div>
              <div className="sm:col-span-2 flex gap-3">
                <button type="submit" className="px-6 py-2 bg-primary text-primary-foreground text-sm rounded-sm hover:bg-primary/90">Create Tour</button>
                <button type="button" onClick={() => setShowCreate(false)} className="px-6 py-2 border border-border/60 text-sm rounded-sm hover:bg-muted">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Tours List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading tours...</div>
        ) : tours.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border/60 rounded-sm">
            <ClipboardList className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-muted-foreground">No tours yet. Create your first tour to get started.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tours.map(tour => {
              const Icon = PROGRAM_ICONS[tour.program_type] || ClipboardList;
              return (
                <Link key={tour.id} to={`/portal/tours/${tour.id}`} data-testid={`tour-${tour.id}`}
                  className="flex items-center gap-4 bg-card border border-border/60 p-5 rounded-sm hover:shadow-md transition-all group">
                  <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-foreground truncate">{tour.name}</h3>
                      <span className={`text-[10px] font-medium uppercase px-2 py-0.5 rounded-full ${statusColors[tour.status] || 'bg-gray-100 text-gray-600'}`}>
                        {tour.status}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{tour.program_type} &middot; {tour.student_count || 0} students &middot; {tour.staff_count || 0} staff</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
